import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ManhwaCard from '@/components/feature/ManhwaCard';
import JsonLD from '@/components/feature/JsonLD';
import { manhwaList } from '@/mocks/manhwa';
import { categories } from '@/mocks/categories';

export default function Home() {
  const featured = manhwaList.filter(m => m.featured).slice(0, 8);
  const trending = [...manhwaList].sort((a, b) => a.trending - b.trending).slice(0, 10);
  const latest = [...manhwaList].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);
  const topRated = [...manhwaList].sort((a, b) => b.rating - a.rating).slice(0, 6);

  const siteJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫之家',
    url: 'http://topshine.net.cn/',
    description: '免费韩漫大全，好看的韩国漫画在线阅读平台。韩漫之家通过漫画网址提供免费韩漫大全与好看韩国漫画的韩漫在线阅读，服务稳定值得信赖。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'http://topshine.net.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: 'http://topshine.net.cn/',
    description: '免费韩漫大全，好看的韩国漫画在线阅读平台，服务稳定值得信赖。',
  };

  const pageJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫之家 - 免费韩漫大全_好看的韩国漫画在线阅读',
    description: '韩漫之家通过漫画网址提供免费韩漫大全与好看韩国漫画的韩漫在线阅读，服务稳定值得信赖。',
    url: 'http://topshine.net.cn/',
    isPartOf: { '@type': 'WebSite', name: '韩漫之家', url: 'http://topshine.net.cn/' },
    about: { '@type': 'Organization', name: '韩漫之家' },
  };

  return (
    <>
      <JsonLD data={[siteJsonLD, orgJsonLD, pageJsonLD]} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative pt-16" aria-labelledby="hero-heading">
            <div className="relative h-[420px] md:h-[560px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20artistic%20collage%20background%2C%20soft%20warm%20gradient%20in%20peach%20and%20cream%20tones%2C%20subtle%20comic%20panel%20overlay%2C%20minimalist%20artistic%20style%2C%20clean%20aesthetic%2C%20modern%20Korean%20illustration%20art%20with%20gentle%20brush%20texture%2C%20warm%20ambient%20glow%2C%20abstract%20storytelling%20vibe&width=1600&height=900&seq=hero-bg-manhwa-home&orientation=landscape"
                alt="韩漫之家 - 免费韩漫大全"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
                <h1 id="hero-heading" className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
                  免费韩漫大全
                </h1>
                <p className="text-base md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
                  好看的韩国漫画在线阅读平台，服务稳定值得信赖
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Link
                    to="/category/all"
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-colors whitespace-nowrap"
                  >
                    立即阅读
                  </Link>
                  <Link
                    to="/about"
                    className="px-8 py-3 bg-background-50/20 hover:bg-background-50/30 text-background-50 backdrop-blur-sm rounded-full font-medium text-sm transition-colors whitespace-nowrap border border-background-50/30"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="bg-background-100 border-b border-background-200/70" aria-label="平台数据">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: '收录漫画', value: '20,000+', icon: 'ri-book-2-line' },
                  { label: '每日更新', value: '500+', icon: 'ri-refresh-line' },
                  { label: '在读用户', value: '800万+', icon: 'ri-user-line' },
                  { label: '累计阅读', value: '15亿+', icon: 'ri-eye-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center">
                      <i className={`${stat.icon} text-xl text-accent-500`}></i>
                    </div>
                    <p className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Manhwa */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12" aria-labelledby="featured-heading">
            <div className="flex items-center justify-between mb-6">
              <h2 id="featured-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                精选推荐
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {featured.map((m) => (
                <ManhwaCard
                  key={m.id}
                  id={m.id}
                  title={m.title}
                  author={m.author}
                  cover={m.cover}
                  rating={m.rating}
                  views={m.views}
                  status={m.status}
                  category={m.category}
                  tags={m.tags}
                  chapters={m.chapters}
                />
              ))}
            </div>
          </section>

          {/* Categories */}
          <section className="bg-background-100 py-12" aria-labelledby="categories-heading">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 id="categories-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
                漫画分类
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 hover:bg-primary-50 transition-colors group"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-lg md:text-xl text-primary-600`}></i>
                    </div>
                    <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12" aria-labelledby="latest-heading">
            <div className="flex items-center justify-between mb-6">
              <h2 id="latest-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                最近更新
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                更多
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latest.map((m) => (
                <ManhwaCard
                  key={m.id}
                  id={m.id}
                  title={m.title}
                  author={m.author}
                  cover={m.cover}
                  rating={m.rating}
                  views={m.views}
                  status={m.status}
                  category={m.category}
                  tags={m.tags}
                  chapters={m.chapters}
                />
              ))}
            </div>
          </section>

          {/* Top Rated Sidebar-style */}
          <section className="bg-background-100 py-12" aria-labelledby="top-heading">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 id="top-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                排行榜
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {topRated.map((m, index) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-background-50 hover:bg-primary-50/50 transition-colors group"
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <span className={`text-lg font-heading font-bold ${
                        index < 3 ? 'text-accent-500' : 'text-foreground-400'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                    <img
                      src={m.cover}
                      alt={m.title}
                      className="w-14 h-20 rounded-lg object-cover object-top flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {m.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{m.author}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-accent-600 font-medium flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-xs"></i>
                          </span>
                          {m.rating}
                        </span>
                        <span className="text-xs text-foreground-500">{m.status}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Trending Tags */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12" aria-labelledby="tags-heading">
            <h2 id="tags-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              热门标签
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {['热血', '恋爱', '奇幻', '校园', '重生', '修仙', '搞笑', '日常', '悬疑', '治愈', '穿越', '武侠', '升级流', '甜宠', '战斗'].map((tag) => (
                <Link
                  key={tag}
                  to={`/category/all`}
                  className="px-5 py-2 bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-background-100 py-14" aria-labelledby="cta-heading">
            <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
              <h2 id="cta-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                发现你喜欢的韩国漫画
              </h2>
              <p className="text-foreground-600 mb-8 leading-relaxed">
                韩漫之家收录海量免费韩漫大全，涵盖恋爱、奇幻、动作、搞笑等多种类型。好看的韩国漫画尽在韩漫之家，随时随地韩漫在线阅读。
              </p>
              <Link
                to="/category/all"
                className="inline-block px-10 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-colors whitespace-nowrap"
              >
                开始探索
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}